import { useState } from 'react';
import snapshot1 from '../assets/Learn41_Snapshot_1.png';
import snapshot2 from '../assets/Learn41_Snapshot_2.png';
import macbookFrame from '../assets/macbook-frame.svg';
import studentArt from '../assets/student.svg';
import collegeArt from '../assets/college.svg';

function scrollTo(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}

const screenshots = [
  { src: snapshot1, alt: 'Learn41 AI interview, candidate and AI interviewer in a live voice conversation' },
  { src: snapshot2, alt: 'Learn41 technical case study interview with AI interviewer' },
];

export default function WhoItsFor() {
  const [activeIndex, setActiveIndex] = useState(0);

  const goTo = (index: number) => {
    setActiveIndex((index + screenshots.length) % screenshots.length);
  };

  return (
    <section id="who-its-for" className="bg-background py-20 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-6">
        {/* Students, illustration left, copy right */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-24">
          <div className="flex justify-center md:justify-start order-1">
            <img src={studentArt} alt="" aria-hidden="true" className="w-full max-w-xs" />
          </div>
          <div className="order-2">
            <h3 className="font-playfair text-3xl md:text-4xl font-medium italic text-foreground mb-5">Students</h3>
            <p className="text-lg text-muted-foreground leading-relaxed mb-5">
              Placements are around the corner. You're shortlisted for Amazon, Infosys, and two more,
              each with a completely different interview style. Generic prep won't cut it.
            </p>
            <ul className="space-y-2.5 text-base text-muted-foreground mb-7">
              <li className="flex items-start gap-2.5">
                <span className="mt-[0.45rem] w-1.5 h-1.5 rounded-full bg-primary-accent-500 shrink-0" />
                Company-specific mock interviews with real voice AI
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-[0.45rem] w-1.5 h-1.5 rounded-full bg-primary-accent-500 shrink-0" />
                Feedback on what that company actually evaluates
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-[0.45rem] w-1.5 h-1.5 rounded-full bg-primary-accent-500 shrink-0" />
                Coding, system design, case study, behavioral, all formats
              </li>
            </ul>
            <a
              href="#try-interview"
              onClick={(e) => scrollTo(e, 'try-interview')}
              className="inline-block bg-primary-accent-500 text-primary font-semibold px-6 py-2.5 rounded-full hover:bg-primary-accent-400 transition-colors"
            >
              Try a Mock Interview
            </a>
          </div>
        </div>

        {/* Colleges, copy left, illustration right */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="order-2 md:order-1">
            <h3 className="font-playfair text-3xl md:text-4xl font-medium italic text-foreground mb-5">Colleges &amp; Universities</h3>
            <p className="text-lg text-muted-foreground leading-relaxed mb-5">
              It's an employer's market. CGPA tells you who scored well on exams,
              not who's ready for Google vs Infosys. Your placement cell deserves better data.
            </p>
            <ul className="space-y-2.5 text-base text-muted-foreground mb-7">
              <li className="flex items-start gap-2.5">
                <span className="mt-[0.45rem] w-1.5 h-1.5 rounded-full bg-primary-accent-500 shrink-0" />
                Assess all students with real interview simulations
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-[0.45rem] w-1.5 h-1.5 rounded-full bg-primary-accent-500 shrink-0" />
                Know who's ready for which companies, skill-based, not grade-based
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-[0.45rem] w-1.5 h-1.5 rounded-full bg-primary-accent-500 shrink-0" />
                Showcase pre-vetted talent to attract higher-tier recruiters
              </li>
            </ul>
            <a
              href="#book-demo"
              onClick={(e) => scrollTo(e, 'book-demo')}
              className="inline-block bg-primary-accent-500 text-primary font-semibold px-6 py-2.5 rounded-full hover:bg-primary-accent-400 transition-colors"
            >
              Book a 15-min Call
            </a>
          </div>
          <div className="flex justify-center md:justify-end order-1 md:order-2">
            <img src={collegeArt} alt="" aria-hidden="true" className="w-full max-w-md" />
          </div>
        </div>

        {/* MacBook carousel */}
        <div className="mt-24 mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-primary-accent-500 font-semibold mb-3">
              The Product
            </p>
            <h3 className="font-playfair text-4xl md:text-5xl font-medium italic text-foreground">
              A glimpse inside
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {/* Left arrow */}
            <button
              onClick={() => goTo(activeIndex - 1)}
              className="shrink-0 w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
              aria-label="Previous screenshot"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            {/* MacBook frame + screenshots */}
            <div className="relative flex-1">
              <img
                src={macbookFrame}
                alt=""
                className="w-full"
                aria-hidden="true"
              />
              {screenshots.map((shot, i) => (
                <img
                  key={i}
                  src={shot.src}
                  alt={shot.alt}
                  className="absolute object-cover transition-opacity duration-500"
                  style={{
                    left: '11.81%',
                    top: '5.27%',
                    width: '76.39%',
                    height: '73.64%',
                    opacity: i === activeIndex ? 1 : 0,
                  }}
                />
              ))}
            </div>

            {/* Right arrow */}
            <button
              onClick={() => goTo(activeIndex + 1)}
              className="shrink-0 w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
              aria-label="Next screenshot"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {screenshots.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === activeIndex
                    ? 'bg-primary-accent-500 w-5'
                    : 'bg-border w-1.5'
                }`}
                aria-label={`Go to screenshot ${i + 1}`}
              />
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
